import {useApolloClient} from '@apollo/client';
import {none, some} from '@danwang/option';
import {
  useAllAccountsQuery,
  useAllCategoriesQuery,
  useAllFieldsQuery,
  useBindingsQuery,
  useCreateCategoryMutation,
  useSetBindingMutation,
} from '@transactions/graphql';
import {ALL_BINDING_KEYS, BINDINGS} from 'common/bindings';
import * as React from 'react';
import AccountSelect from 'src/AccountSelect';
import {isAccountClosed} from 'src/AccountsView/accountFilter';
import {addNewCategory} from 'src/apolloCache/updateCategories';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import CategoryPicker from 'src/CategoryPicker';
import {FilterDropdown} from 'src/components/FilterDropdown';
import type {
  AllAccountsQuery,
  AllCategoriesQuery,
  AllFieldsQuery,
} from '@transactions/graphql';
import type {BindingKey} from 'common/bindings';

/**
 * The Defaults page: every binding slot the code declares
 * (common/bindings.ts), with the entity it currently resolves to. Slots are
 * normally written implicitly — a flow remembers the choice the first time
 * it runs — so this page is where those remembered defaults are audited,
 * corrected, or set ahead of time. The registry is the row source: a slot
 * appears here the moment a flow declares it, bound or not.
 */

type SetBindingFn = (key: BindingKey, entityId: string | null) => void;
type CreateAndBindCategoryFn = (key: BindingKey, name: string) => void;

const BindingValuePicker = ({
  bindingKey,
  entityId,
  accounts,
  categories,
  fields,
  onSet,
  onCreateAndBindCategory,
}: {
  bindingKey: BindingKey;
  entityId: string | null;
  accounts: AllAccountsQuery['allAccounts'];
  categories: AllCategoriesQuery['allCategories'];
  fields: AllFieldsQuery['allFields'];
  onSet: SetBindingFn;
  onCreateAndBindCategory: CreateAndBindCategoryFn;
}) => {
  const spec = BINDINGS[bindingKey];
  // Re-selecting the current value is a no-op, not a write: AccountSelect
  // opens on focus and submits its first match on Tab, so a keyboard pass
  // through the page must never rewrite (or worse, re-point) a binding the
  // user didn't touch.
  const handleChange = (nextEntityId: string | null) => {
    if (nextEntityId !== entityId) {
      onSet(bindingKey, nextEntityId);
    }
  };
  if (spec.entityType === 'account') {
    const bound = accounts.find((account) => account._id === entityId);
    return (
      <AccountSelect
        selectedAccount={bound ? some(bound) : none}
        // Slots whose flow is single-currency only offer matching accounts —
        // binding a EUR account to a USD import would just make the import
        // fail the server's currency guard later.
        currency={spec.currency}
        onSelectAccount={(selection) =>
          handleChange(selection.map((account) => account._id).getOrElse(null))
        }
      />
    );
  }
  if (spec.entityType === 'field') {
    return (
      <FilterDropdown
        label="Field"
        allLabel="Not set"
        options={fields.map((field) => ({key: field._id, label: field.name}))}
        value={entityId ?? 'all'}
        onChange={(value) => handleChange(value === 'all' ? null : value)}
      />
    );
  }
  // Bindings store entity ids while CategoryPicker trades in names, so the
  // row maps between the two. A name that matches no category is the
  // picker's Add "…" path — the category is created and then bound, never
  // silently read as a clear (only an explicitly emptied picker clears).
  const boundCategory = categories.find((category) => category._id === entityId);
  return (
    <CategoryPicker
      selectedCategory={boundCategory ? some(boundCategory.name) : none}
      onSelectCategory={(selection) => {
        if (selection.isEmpty) {
          handleChange(null);
          return;
        }
        selection.forEach((name) => {
          const existing = categories.find(
            (category) => category.name === name,
          );
          if (existing) {
            handleChange(existing._id);
          } else {
            onCreateAndBindCategory(bindingKey, name);
          }
        });
      }}
    />
  );
};

const BindingRow = ({
  bindingKey,
  entityId,
  accounts,
  categories,
  fields,
  entitiesReady,
  onSet,
  onCreateAndBindCategory,
}: {
  bindingKey: BindingKey;
  entityId: string | null;
  accounts: AllAccountsQuery['allAccounts'];
  categories: AllCategoriesQuery['allCategories'];
  fields: AllFieldsQuery['allFields'];
  /** Whether the entity list for this row's type has actually loaded. */
  entitiesReady: boolean;
  onSet: SetBindingFn;
  onCreateAndBindCategory: CreateAndBindCategoryFn;
}) => {
  const labelId = React.useId();
  const spec = BINDINGS[bindingKey];
  // A bound id whose entity no longer exists renders an empty picker, which
  // alone would misread as "not set" — say what happened and offer the way
  // out. Only once the entity list has loaded, though: an unresolved (or
  // failed) query is not evidence of deletion, and offering Clear on that
  // false reading would let a slow connection destroy a valid binding.
  const dangling =
    entitiesReady &&
    entityId != null &&
    (spec.entityType === 'account'
      ? !accounts.some((account) => account._id === entityId)
      : spec.entityType === 'field'
        ? !fields.some((field) => field._id === entityId)
        : !categories.some((category) => category._id === entityId));
  // A closed account still exists, but every flow that reads its binding
  // deliberately skips it (a closed account accepts no new rows) — showing
  // it here as quietly valid would misrepresent what the flow will do.
  const boundAccount =
    spec.entityType === 'account'
      ? accounts.find((account) => account._id === entityId)
      : undefined;
  const closedBound = boundAccount != null && isAccountClosed(boundAccount);
  // setBinding now refuses wrong-currency accounts, but rows stored before
  // that guard (or before a slot declared its currency) can still hold one
  // — and the flows deliberately skip it, so it must not present as
  // healthy.
  const currencyMismatch =
    boundAccount != null &&
    spec.currency != null &&
    boundAccount.currency !== spec.currency;

  return (
    <div className="border-border flex flex-col justify-between gap-2 border-b py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-6">
      <div className="min-w-0">
        <div id={labelId} className="text-foreground text-sm font-medium">
          {spec.label}
        </div>
        <div className="text-muted-foreground text-sm">{spec.description}</div>
        {entityId == null && (
          <div className="text-muted-foreground mt-0.5 text-xs">
            Not set — remembered the first time the flow runs
          </div>
        )}
        {dangling && (
          <div className="text-destructive mt-0.5 text-xs">
            The bound {spec.entityType} no longer exists — pick another or
            clear it.
          </div>
        )}
        {closedBound && (
          <div className="text-destructive mt-0.5 text-xs">
            The bound account is closed, so flows skip it — pick another or
            clear it.
          </div>
        )}
        {currencyMismatch && boundAccount && spec.currency && (
          <div className="text-destructive mt-0.5 text-xs">
            The bound account is denominated in {boundAccount.currency}, but
            this slot requires {spec.currency} — flows skip it; pick another
            or clear it.
          </div>
        )}
      </div>
      {/* The visual label is a plain div, so the picker group carries an
          explicit association — otherwise every row's control announces as
          an identical "Select account...". */}
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex shrink-0 items-center gap-2 sm:w-72"
      >
        <div className="min-w-0 flex-1">
          {entitiesReady || entityId == null ? (
            <BindingValuePicker
              bindingKey={bindingKey}
              entityId={entityId}
              accounts={accounts}
              categories={categories}
              fields={fields}
              onSet={onSet}
              onCreateAndBindCategory={onCreateAndBindCategory}
            />
          ) : (
            // A bound id with no loaded entity list must not render an
            // interactive picker: it would show empty, and tabbing out of
            // an empty search commits a clear (SearchBox invokes onClear
            // on blur) — silently destroying a valid binding.
            <div className="text-muted-foreground py-1.5 text-sm">
              Loading…
            </div>
          )}
        </div>
        {(dangling || closedBound || currencyMismatch) && (
          <Button
            variant="outline"
            className="px-3 py-1.5 text-sm"
            onClick={() => onSet(bindingKey, null)}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export const DefaultsView = () => {
  useBreadcrumb({
    label: 'Defaults',
    fallback: [{label: 'Settings', href: '/settings'}],
  });
  const client = useApolloClient();
  const {
    data: bindingsData,
    loading,
    error: bindingsError,
    refetch,
  } = useBindingsQuery();
  const {data: accountsData} = useAllAccountsQuery();
  const {data: categoriesData} = useAllCategoriesQuery();
  const {data: fieldsData} = useAllFieldsQuery();
  const [setBinding] = useSetBindingMutation();
  const [createCategory] = useCreateCategoryMutation();
  const [error, setError] = React.useState<string | null>(null);

  // Writes are chained: the server resolver rewrites the whole bindings
  // array from what it read, so two in-flight setBinding calls could each
  // read the same original and the later one silently drop the other's
  // edit. Serializing here closes that within this page; cross-tab writes
  // stay last-write-wins, the same deliberate trade-off documented on the
  // categoryGroups resolver.
  const pendingWrite = React.useRef<Promise<unknown>>(Promise.resolve());
  // Intent guard for the async create+bind below: every direct change to a
  // row bumps its token, and a category creation only binds if its token is
  // still current — so a slow create can never overwrite a newer choice the
  // user made while it was in flight.
  const bindingIntents = React.useRef(new Map<BindingKey, number>());
  const bumpIntent = (key: BindingKey): number => {
    const next = (bindingIntents.current.get(key) ?? 0) + 1;
    bindingIntents.current.set(key, next);
    return next;
  };
  const handleSet: SetBindingFn = (key, entityId) => {
    setError(null);
    bumpIntent(key);
    pendingWrite.current = pendingWrite.current
      .then(() => setBinding({variables: {key, entityId}}))
      .then(() => {
        // Root-field evict is the app's idiom for refreshing this query; the
        // active useBindingsQuery refetches and the row re-renders.
        client.cache.evict({fieldName: 'bindings'});
      })
      .catch((err: unknown) => {
        // Also settles the chain, so a failed write never wedges later ones.
        setError(err instanceof Error ? err.message : 'Failed to save');
      });
  };

  // The category picker's Add "…" path: create the category first, then
  // bind its id — mirroring the inline get-or-create the transaction forms
  // do by name. The refetch keeps the just-created category in the row's
  // list, so its name resolves on the next render.
  const handleCreateAndBindCategory: CreateAndBindCategoryFn = (key, name) => {
    setError(null);
    const intent = bumpIntent(key);
    createCategory({
      variables: {name},
      // Written into the cache synchronously (not refetched): the bound id
      // must resolve against the category list the moment the row
      // re-renders, or a valid fresh binding would present as dangling
      // until some later refetch happened to succeed.
      update: (store, result) => {
        const created = result.data?.createCategory;
        if (created) {
          addNewCategory(store, created);
        }
      },
    })
      .then((result) => {
        const created = result.data?.createCategory;
        if (!created) return;
        // A newer selection on this row superseded the Add while the
        // category was being created — the category exists (harmless),
        // but it must not overwrite the newer choice.
        if (bindingIntents.current.get(key) === intent) {
          handleSet(key, created._id);
        }
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : 'Failed to create category',
        );
      });
  };

  if (loading && bindingsData == null) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading defaults...</span>
      </div>
    );
  }

  // A failed bindings query is not an empty configuration — rendering the
  // rows as "Not set" would invite edits made against false state.
  if (bindingsData == null) {
    return (
      <div className="flex flex-col items-center gap-3 p-8">
        <span className="text-destructive text-sm">
          Failed to load defaults
          {bindingsError ? `: ${bindingsError.message}` : ''}
        </span>
        <Button variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const bindings = bindingsData?.bindings ?? [];
  const boundEntityId = (key: BindingKey): string | null =>
    bindings.find((binding) => binding.key === key)?.entityId ?? null;

  return (
    // flex-1 + overflow-y-auto: MainPane puts routes in a flex column with
    // overflow-hidden, so the page must own its vertical scrolling or rows
    // past the viewport are unreachable.
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div>
        <h2 className="text-lg font-semibold">Defaults</h2>
        <p className="text-muted-foreground text-sm">
          Accounts and categories that recurring flows target — remembered
          automatically when a flow first runs, adjustable here
        </p>
      </div>
      {error && <div className="text-destructive text-sm">{error}</div>}
      <div className="border-border rounded-lg border px-4">
        {ALL_BINDING_KEYS.map((bindingKey) => (
          <BindingRow
            key={bindingKey}
            bindingKey={bindingKey}
            entityId={boundEntityId(bindingKey)}
            accounts={accountsData?.allAccounts ?? []}
            categories={categoriesData?.allCategories ?? []}
            fields={fieldsData?.allFields ?? []}
            entitiesReady={
              BINDINGS[bindingKey].entityType === 'account'
                ? accountsData != null
                : BINDINGS[bindingKey].entityType === 'field'
                  ? fieldsData != null
                  : categoriesData != null
            }
            onSet={handleSet}
            onCreateAndBindCategory={handleCreateAndBindCategory}
          />
        ))}
      </div>
    </div>
  );
};
